import {
  Currency,
  CurrencyAmount,
  Field,
  tryParseAmount
} from '@cryptoalgebra/kim-sdk';
import { Address } from '@thirdweb-dev/sdk';
import { useRouter } from 'next/router';
import { useProviderStrategies } from '@app/hooks/plugins/useProviderStrategies';
import { useStrategyData } from '@app/hooks/plugins/useStrategyData';
import { useVaultApr } from '@app/hooks/plugins/useVaultApr';
import { useVaultDetailsByAddress } from '@app/hooks/plugins/useVaultDetailsByAddress';
import { useVaults } from '@app/hooks/plugins/useVaults';
import { useConfig } from '@app/config';
import { useBalance, useContract } from '@thirdweb-dev/react';
import { useAsyncFn } from 'react-use';
import { EMPTY_ADDRESS } from '@app/constants/common';
import { useIsNitroPool } from '@app/hooks/nitro/useIsNitroPool';
import { useEffect, useMemo, useRef } from 'react';
import { usePool } from '@app/hooks/v3/usePool';
import { ethers } from 'ethers';
import { trimDecimalsOverflow } from '@app/helpers/decimals';
import fromExponential from 'from-exponential';
import { IDerivedMintInfoWithProvider } from '@app/state/mintStore';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';
import { useTokensRatiosForSteer } from '@app/hooks/plugins/useTokensRatiosForSteer';
import { useAlgebraPoolNitroDetails } from '@app/hooks/nitro/useAlgebraPoolNitroDetails';

export function useDerivedSteerInfo(
  poolAddress?: Address,
  baseCurrency?: Currency
): IDerivedMintInfoWithProvider {
  const { query } = useRouter();
  const {
    currencyA,
    currencyB,
    strategy,
    typedValue,
    independentField,
    mode,
    provider,
    actions: { setStrategy, setMode }
  } = useLiquidityWizardState();
  const dependentField =
    independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A;
  const { strategies } = useProviderStrategies(poolAddress);

  const selectedStrategy = useMemo(() => {
    return strategies?.find(
      item => item.vaultAddress.toLowerCase() === strategy?.toLowerCase()
    );
  }, [strategies, strategy]);

  const { data: strategyData } = useStrategyData(
    selectedStrategy?.strategyIpfsHash
  );

  const { data: vaultApr } = useVaultApr(strategy);
  const { data: vaultDetails } = useVaultDetailsByAddress(strategy);
  const { data: vaults } = useVaults();
  const config = useConfig();

  const selectedVault = useMemo(() => {
    return vaults?.find(
      item => item.id?.toLowerCase() === strategy?.toLowerCase()
    );
  }, [vaults, strategy]);

  const { contract: poolFactoryContract } = useContract(
    config?.CONTRACTS.NFT_POOL_FACTORY
  );

  const [{ value: nftPoolAddress }, getPoolAddress] = useAsyncFn(async () => {
    if (!poolFactoryContract || !strategy) {
      return;
    }

    const nftPoolAddress = await poolFactoryContract.call('getPool', [
      strategy
    ]);

    return nftPoolAddress === EMPTY_ADDRESS ? null : nftPoolAddress;
  }, [poolFactoryContract, strategy]);

  const nitroDetails = useAlgebraPoolNitroDetails(nftPoolAddress);

  const { isNitroPool } = useIsNitroPool(nftPoolAddress);

  useEffect(() => {
    getPoolAddress();
  }, [getPoolAddress]);

  useEffect(() => {
    if (
      strategies &&
      strategies.length &&
      !strategy &&
      mode === 'auto' &&
      provider === 'steer'
    ) {
      // set first available strategy as default selection
      setStrategy(strategies[0]?.vaultAddress);
    }
  }, [mode, provider, setStrategy, strategies, strategy]);

  const vaultSetRef = useRef(false);

  useEffect(() => {
    if (
      query &&
      query.vaultId &&
      mode === 'manual' &&
      strategies &&
      !vaultSetRef.current
    ) {
      const { vaultId } = query;
      const str = strategies?.find(s => s.vaultAddress === vaultId);

      if (str) {
        setMode('auto');
        setStrategy(str.vaultAddress);
        vaultSetRef.current = true;
      }
    }
  }, [query.vaultId, vaults, strategies, mode, query, setMode, setStrategy]);

  const { data: tokensRatio } = useTokensRatiosForSteer(
    poolAddress,
    currencyA,
    currencyB
  );

  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA,
      [Field.CURRENCY_B]: currencyB
    }),
    [currencyA, currencyB]
  );

  // formatted with tokens
  const [token0, token1] = useMemo(
    () => [currencyA, currencyB, baseCurrency],
    [currencyA, currencyB, baseCurrency]
  );

  const { data: token0Balance } = useBalance(
    token0?.isNative ? '' : token0?.wrapped.address
  );
  const { data: token1Balance } = useBalance(
    token1?.isNative ? '' : token1?.wrapped.address
  );
  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]:
      token0 && token0Balance
        ? CurrencyAmount.fromRawAmount(token0, token0Balance.value.toString())
        : undefined,
    [Field.CURRENCY_B]:
      token1 && token1Balance
        ? CurrencyAmount.fromRawAmount(token1, token1Balance.value.toString())
        : undefined
  };

  const [, pool] = usePool(poolAddress as Address);

  const independentAmount: CurrencyAmount<Currency> | undefined =
    tryParseAmount(typedValue, currencies[independentField]);

  const dependentAmount = useMemo(() => {
    if (!independentAmount || !tokensRatio || !currencies[dependentField]) {
      return;
    }

    const v = +independentAmount.toExact() / +tokensRatio.ratio;

    const amount = ethers.utils.parseUnits(
      trimDecimalsOverflow(
        fromExponential(v.toString()),
        currencies[dependentField]!.decimals
      ),
      currencies[dependentField]!.decimals
    );

    return currencies[dependentField]
      ? CurrencyAmount.fromRawAmount(
          currencies[dependentField]!,
          amount.toString()
        )
      : undefined;
  }, [independentAmount, tokensRatio, currencies, dependentField]);

  const parsedAmounts: {
    [field in Field]: CurrencyAmount<Currency> | undefined;
  } = useMemo(() => {
    return {
      [Field.CURRENCY_A]:
        independentField === Field.CURRENCY_A
          ? independentAmount
          : dependentAmount,
      [Field.CURRENCY_B]:
        independentField === Field.CURRENCY_A
          ? dependentAmount
          : independentAmount
    };
  }, [dependentAmount, independentAmount, independentField]);

  const isSwappedPair = useMemo(() => {
    if (!pool) {
      return false;
    }

    return (
      pool.token0.address.toLowerCase() !==
      currencyA?.wrapped.address.toLowerCase()
    );
  }, [pool, currencyA]);

  let errorMessage;

  if (
    parsedAmounts.CURRENCY_A &&
    currencyBalances.CURRENCY_A?.lessThan(parsedAmounts.CURRENCY_A)
  ) {
    errorMessage = `Insufficient ${currencyA?.symbol} balance`;
  }

  if (
    parsedAmounts.CURRENCY_B &&
    currencyBalances.CURRENCY_B?.lessThan(parsedAmounts.CURRENCY_B)
  ) {
    errorMessage = `Insufficient ${currencyB?.symbol} balance`;
  }

  return {
    pool,
    token0,
    token1,
    currencyBalances,
    strategies,
    strategyData,
    vaultApr,
    vaultDetails,
    parsedAmounts,
    independentField,
    dependentField,
    vaultAddress: strategy ?? undefined,
    isSwappedPair,
    selectedVault,
    errorMessage,
    nftPoolAddress,
    getPoolAddress,
    isNitroPool: Boolean(isNitroPool && nitroDetails),
    lockDuration: 0,
    nitroDetails
  };
}
